import { useAppQuery } from '@/helpers/api/query'
import { createInstance } from '@/helpers/api/base'
import { useQueryClient } from '@tanstack/react-query'

const GET_REFERENCE_KEY = 'reference'

// Temporary references are six random numeric characters
const generateTemporaryReference = (): string => {
    const chars = '0123456789'
    let ref = ''
    for (let i = 0; i < 6; i += 1) {
        const idx = Math.floor(Math.random() * chars.length)
        ref += chars[idx]
    }
    return ref
}

export const useGetReference = () => {
    const queryClient = useQueryClient()

    const query = useAppQuery<string>({
        queryKey: [GET_REFERENCE_KEY],
        queryFn: () =>
            createInstance({
                url: '/controller-logs/reference',
                method: 'GET',
                sendWhileOffline: true,
            }),
        staleTime: Infinity,
        networkMode: 'always',
    })

    // Get cached value and ensure it matches the required format [0-9A-Z]{4,6}.
    // If it doesn't, use a temporary reference instead and update the cache to keep consumers consistent.
    // Note: numeric references (e.g. "00042") are parsed as numbers by JSON.parse/Axios,
    // so we coerce to string and pad to 5 digits to restore leading zeros.
    const raw = queryClient.getQueryData<string | number>([GET_REFERENCE_KEY])
    const cached = typeof raw === 'number' ? String(raw).padStart(5, '0') : raw
    const isValid = typeof cached === 'string' && /^[0-9A-Z]{4,6}$/.test(cached)
    const data = isValid ? cached : generateTemporaryReference()

    const discardReference = () => {
        const newTempRef = generateTemporaryReference()
        queryClient.setQueryData([GET_REFERENCE_KEY], newTempRef)
        queryClient.invalidateQueries({ queryKey: [GET_REFERENCE_KEY] })
    }

    return {
        ...query,
        data,
        discardReference,
    }
}
